<template>
  <div>
    <button
      v-if="reviewsCount"
      type="button"
      class="star-rating-popup__button"
      :aria-label="accessibleTitle"
      @click.prevent="handleOpenOverlay"
    >
      <star-rating :loading="loading" :product="product" />
    </button>

    <teleport to="body">
      <overlay
        direction="up"
        block-class="review-info-overlay"
        type="modal"
        :namespace="`reviewInfoModal-${getProductOrVariantId}`"
      >
        <template #body>
          <div class="review-info-overlay__content">
            <div>
              <star-rating :loading="loading" :product="product" />
            </div>
            <p class="review-info-overlay__title" v-text="reviewsPopupTitle" />
            <p
              class="review-info-overlay__description"
              v-text="reviewsPopupText"
            />
            <p
              v-html="$string('product.product_card.reviews_popup_link_html')"
            />
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Star rating link (star-rating)
 * -----------------------------------------------------------------------------
 * Shows linked product rating (metafields).
 *
 * @param {Boolean} [loading] - Show loading state.
 * @param {Boolean|Object} [product] - Formatted product.
 *
 */
import StarRating from '~global/star-rating/star-rating';
import { mapActions } from 'pinia';
import Overlay from '~global/overlay/overlay';
import useOverlaysStore from '~stores/overlays';

export default {
  name: 'StarRatingPopup',

  components: {
    StarRating,
    Overlay,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  computed: {
    accessibleTitle() {
      return this.$string('accessibility.reviews_count', {
        replace: { count: this.reviewsCount },
      });
    },
    /**
     * Compute reviews count number.
     * @returns {Number}
     */
    reviewsCount() {
      return Number(this.product.reviews?.count) || 0;
    },

    reviewsPopupTitle() {
      return this.$string('product.product_card.reviews_popup_title', {
        replace: { value: this.product.reviews?.rating || 0 },
      });
    },

    reviewsPopupText() {
      return this.$string('product.product_card.reviews_popup_text', {
        replace: { count: this.reviewsCount },
      });
    },

    getProductOrVariantId() {
      return this.product.selectedOrFirstAvailableVariant
        ? this.product.selectedOrFirstAvailableVariant.id
        : this.product.id;
    },
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
      closeOverlay: 'close',
    }),

    /**
     * Handle open overlay.
     */
    handleOpenOverlay() {
      const productOrVariantId = this.product.selectedOrFirstAvailableVariant
        ? this.product.selectedOrFirstAvailableVariant.id
        : this.product.id;
      this.openOverlay({
        ignoreDismissed: true,
        namespace: `reviewInfoModal-${productOrVariantId}`,
      });
    },

    /**
     * Handle close overlay.
     */
    handleCloseOverlay() {
      const productOrVariantId = this.product.selectedOrFirstAvailableVariant
        ? this.product.selectedOrFirstAvailableVariant.id
        : this.product.id;
      this.closeOverlay(`reviewInfoModal-${productOrVariantId}`);
    },
  },
};
</script>
